import { api } from "./api";

type Page<T> = T[] | { results: T[]; next?: string | null };

const apiPath = (url: string) => {
  if (!url.startsWith("http")) return url;
  const parsed = new URL(url);
  return `${parsed.pathname.replace(/^\/api/, "")}${parsed.search}`;
};

/** Triggers a browser download for an endpoint that returns a file. Reads the
 * filename from Content-Disposition when the server sets one. */
const downloadFile = async (path: string, fallbackName: string) => {
  const response = await api.get(path, { responseType: "blob" });
  const disposition = String(response.headers["content-disposition"] || "");
  const match = disposition.match(/filename="?([^";]+)"?/);
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = match?.[1] || fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** Triggers a browser download of a report export (?export=csv|pdf). */
const downloadReport = async (path: string, format: "csv" | "pdf") => {
  const separator = path.includes("?") ? "&" : "?";
  return downloadFile(`${path}${separator}export=${format}`, `report.${format}`);
};

import { getPage } from "./pagination";

// The largest page the server gives, so a whole collection loads in as few
// requests as possible: at the default of 20, a year's invoices for a full roll
// took 229 requests.
const FULL_PAGE = 100;

export type ListParams = Record<string, string | number | boolean | undefined>;

// "all" is what the filter dropdowns hold when nothing is chosen; it is not a value the server knows.
const listParams = (params?: ListParams) =>
  Object.fromEntries(
    Object.entries(params ?? {}).filter(([, value]) => value !== undefined && value !== "" && value !== "all"),
  );

const withParams = (path: string, params?: ListParams) => {
  const query = new URLSearchParams(
    Object.entries(listParams(params)).map(([key, value]) => [key, String(value)]),
  ).toString();
  return query ? `${path}?${query}` : path;
};
const withFullPages = (path: string) =>
  path.includes("page_size=") ? path : `${path}${path.includes("?") ? "&" : "?"}page_size=${FULL_PAGE}`;

const allRows = async <T>(path: string): Promise<T[]> => {
  const result: T[] = [];
  let next: string | null = withFullPages(path);
  while (next) {
    const data = (await api.get(apiPath(next))).data as Page<T>;
    if (Array.isArray(data)) return [...result, ...data];
    result.push(...data.results);
    next = data.next || null;
  }
  return result;
};
export interface Fee {
  id: number;
  name: string;
  fee_type: string;
  amount: string;
  description?: string;
  is_active: boolean;
  allows_partial_payment: boolean;
  /** Which learners are billed this fee. */
  applies_to?: "all" | "day" | "boarding";
  /** Billed only to learners who use school transport. */
  transport_only?: boolean;
  income_account?: number | null;
}
export interface LedgerPayment {
  id: number;
  student: number;
  student_name: string;
  fee?: number | null;
  fee_name?: string | null;
  receipt_account?: number | null;
  receipt_account_code?: string;
  receipt_account_name?: string;
  external_payment?: number | null;
  refunded_at?: string | null;
  refunded_amount: string;
  refundable_amount: string;
  allocated_amount: string;
  unallocated_amount: string;
  credit_held_amount: string;
  allocations: PaymentAllocation[];
  refund_reason?: string;
  invoice?: number | null;
  invoice_number?: string;
  amount: string;
  payment_method: string;
  payment_status: string;
  payment_date?: string;
  receipt_number?: string;
  reference_number?: string;
  recorded_by_name?: string;
}
export interface PaymentAllocation {
  id: number;
  payment: number;
  invoice: number;
  invoice_number: string;
  amount: string;
  refunded_amount: string;
  active_amount: string;
  reason?: string;
  allocated_by_name?: string;
  allocated_at: string;
}
export interface UnidentifiedReceipt {
  id: number;
  reference: string;
  payer_name?: string;
  amount: string;
  payment_method: string;
  receipt_account: number;
  receipt_account_name: string;
  received_at: string;
  notes?: string;
  receivable_account?: number | null;
  status: "unresolved" | "resolved" | "refunded";
  recorded_by: number;
  recorded_by_name: string;
  resolved_by_name?: string;
  resolved_at?: string;
  resolution_notes?: string;
  student?: number | null;
  student_name?: string;
  invoice?: number | null;
  invoice_number?: string;
  payment?: number | null;
  payment_reference?: string;
}
export interface InvoiceLine {
  id: number;
  fee?: number | null;
  fee_name?: string | null;
  description: string;
  quantity: number;
  unit_amount: string;
  line_total: string;
  allows_partial_payment: boolean;
}
export interface Invoice {
  id: number;
  student: number;
  student_name: string;
  invoice_number: string;
  due_date: string;
  status: string;
  total_amount: string;
  paid_amount: string;
  credited_amount: string;
  balance_due: string;
  notes?: string;
  fee_structure?: number | null;
  fee_structure_name?: string | null;
  lines?: InvoiceLine[];
  allows_partial_payment: boolean;
  /** The least a payment may be now: full-payment-only fees are paid first. */
  minimum_payment?: number | string;
  /** The student's current class, for the register. */
  class_name?: string | null;
  /** When the invoice was issued. */
  created_at?: string;
  cancelled_at?: string | null;
  cancellation_reason?: string;
}
export interface FeeStructureItem {
  id?: number;
  fee: number;
  fee_name?: string;
  fee_type?: string;
  amount: string;
  order?: number;
  allows_partial_payment?: boolean;
  applies_to?: "all" | "day" | "boarding";
  transport_only?: boolean;
}
export interface FeeStructure {
  id: number;
  name: string;
  /** Derived from the term on the server; never sent. */
  academic_year: number;
  academic_year_name?: string;
  /** The AcademicTerm this structure bills for. */
  term: number;
  term_name?: string;
  level: string;
  is_active: boolean;
  notes?: string;
  created_by?: number;
  total_amount: string;
  items: FeeStructureItem[];
  pending_invoice_count: number;
}
export interface GenerateInvoicesResult {
  created_count: number;
  skipped_count: number;
  total_students: number;
  invoice_ids: number[];
  discounted_count?: number;
  discount_total?: string;
  /** Learners in the level with no fee in the structure that applies to them. */
  no_fees_count?: number;
}
/** A learner an invoice run from a fee structure would bill. */
export interface FeeStructureStudent {
  id: number;
  name: string;
  student_id: string;
  class_name: string;
  already_invoiced: boolean;
  /** What this learner would be billed: only the fees that apply to them. */
  invoice_total: string;
}
/** A discount for one student, given as a fixed amount or a percentage. */
export interface GenerationDiscount {
  student: number;
  reason: string;
  amount?: string;
  percent?: string;
}
export interface OnlinePaymentAvailability {
  enabled: boolean;
  /** Why checkout is unavailable, shown to the parent in place of a button. */
  message: string;
}
export interface OnlinePaymentInit {
  order_tracking_id: string;
  payment_url: string;
  amount: string;
  status: string;
}
export interface OnlinePaymentStatus {
  status: string;
  invoice: number;
  amount: string;
  balance_due: string;
}
export interface RefundRequest {
  id: number;
  request_number: string;
  receipt: number;
  receipt_reference?: string;
  student_name: string;
  amount: string;
  reason: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  requested_by: number;
  requested_by_name: string;
  reviewed_by?: number | null;
  reviewed_by_name?: string;
  reviewed_at?: string | null;
  review_notes?: string;
  returned_by_name?: string;
  returned_at?: string | null;
  return_reason?: string;
  /** When it was requested. */
  created_at?: string;
}
export interface CreditNote extends WorkflowFields {
  id: number;
  credit_number: string;
  invoice: number;
  invoice_number: string;
  student_name: string;
  amount: string;
  reason: string;
  status: "draft" | "approved" | "rejected";
  prepared_by: number;
  prepared_by_name: string;
  approved_by_name?: string;
  approved_at?: string | null;
  rejection_reason?: string;
  /** When it was prepared. */
  created_at?: string;
}
export interface FinancialStats {
  total_revenue: string | number;
  total_collected: string | number;
  total_billed: string | number;
  outstanding_balance: string | number;
  pending_payments: number;
  overdue_invoices: number;
  overdue_amount: string | number;
  collection_rate: number;
  payment_methods: {
    payment_method: string;
    count: number;
    total_amount: string;
  }[];
}
export interface StudentOption {
  id: number;
  full_name: string;
  student_id: string;
  admission_number?: string;
  current_class?: number | null;
  class_name?: string | null;
  /** The class's place in school order (Baby, Middle, Top, P1 ...). */
  class_level_order?: number | null;
  gender?: string;
  /** active, suspended, withdrawn, graduated, transferred, expelled or inactive. */
  status?: string;
}
export interface StudentDirectoryPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: StudentOption[];
}
/** Shared by every document type wired to the approval-workflow engine. */
export interface WorkflowFields {
  // Always present on a real API response; optional here only so this type
  // can double as the base for create-payload shapes (Omit<T, "id" | ...>),
  // since it's server-set and never client-supplied.
  workflow_status?:
    | "draft"
    | "submitted"
    | "returned"
    | "verified"
    | "approved"
    | "posted"
    | "rejected"
    | "reversed"
    | "cancelled";
  reviewed_by_name?: string;
  reviewed_at?: string | null;
  review_comments?: string;
  rejected_by_name?: string;
  rejected_at?: string | null;
  rejection_reason?: string;
  returned_by_name?: string;
  returned_at?: string | null;
  return_reason?: string;
}

export interface Account extends WorkflowFields {
  /** False for accounts no payment may leave from, such as Salary advances owed by staff. */
  can_pay_from?: boolean;
  /** True only for accounts a fee structure may post what students owe to. */
  can_hold_student_receivables?: boolean;
  id: number;
  code: string;
  name: string;
  account_type: "asset" | "liability" | "equity" | "income" | "expense";
  parent?: number | null;
  description?: string;
  is_subaccount?: boolean;
  /** Write-only on create: states main vs sub rather than inferring it from `parent`. */
  structure?: "main" | "sub";
  statement_category?: number | null;
  statement_category_name?: string;
  statement_section_name?: string;
  status: "active" | "inactive" | "closed" | "archived";
  pending_status?: string;
  is_active: boolean;
  is_control_account: boolean;
  is_reporting_account: boolean;
  normal_balance: "debit" | "credit";
  is_contra_account: boolean;
  /** Counts as cash in the cash flow statement, and marks the accounts that
   * money received is allowed to land in. Stamped when a receipt role is
   * assigned; editable, since a school may hold cash somewhere with no role. */
  is_cash_equivalent: boolean;
  transaction_count: number;
  initiated_by?: number;
  initiated_by_name?: string;
  initiated_at?: string | null;
  approved_by_name?: string;
  approved_at?: string | null;
}
export interface JournalLine {
  id?: number;
  account: number;
  account_code?: string;
  account_name?: string;
  description?: string;
  debit: string;
  credit: string;
}
export interface JournalLineInput {
  account: number;
  description?: string;
  effect?: "increase" | "decrease";
  amount?: string;
  debit?: string;
  credit?: string;
}
export interface JournalEntry extends WorkflowFields {
  id: number;
  entry_number: string;
  entry_date: string;
  description: string;
  reference?: string;
  transaction_type: "general" | "internal_transfer" | "immediate_expense" | "correction";
  status: "draft" | "posted" | "reversed";
  source_type?: string;
  source_id?: string;
  created_by: number;
  lines: JournalLine[];
  created_by_name: string;
  posted_by_name?: string;
}
export interface AccountBrief {
  id: number;
  code: string;
  name: string;
  account_type: Account["account_type"];
  is_active: boolean;
}
export interface AccountRoleAssignment {
  role: string;
  label: string;
  help_text: string;
  group: string;
  account_type: Account["account_type"];
  is_control: boolean;
  is_cash_equivalent: boolean;
  account: AccountBrief | null;
  pending_account: AccountBrief | null;
  /** "unassigned" means nobody has proposed an account for this role yet. */
  workflow_status: NonNullable<WorkflowFields["workflow_status"]> | "unassigned";
  is_assigned: boolean;
  initiated_by_name: string;
  reviewed_by_name: string;
  approved_by_name: string;
  review_comments: string;
  return_reason: string;
  rejection_reason: string;
  updated_at: string | null;
}
export interface BulkRoleSubmission {
  submitted?: string[];
  submitted_count?: number;
  verified?: string[];
  verified_count?: number;
  approved?: string[];
  approved_count?: number;
  /** Roles left behind, with why their proposed account could not be sent on. */
  skipped: Array<{ role: string; message: string }>;
}
export interface AccountRoleListing {
  groups: string[];
  roles: AccountRoleAssignment[];
  unassigned_count: number;
}
export interface StatementLine {
  id: number;
  code: string;
  name: string;
  account_type: Account["account_type"];
  amount: string;
  comparative_amount: string | null;
}
export interface StatementCategoryLines {
  id: number;
  name: string;
  accounts: StatementLine[];
  total: string;
  comparative_total: string | null;
}
export interface StatementSectionLines {
  id: number;
  name: string;
  categories: StatementCategoryLines[];
  total: string;
  comparative_total: string | null;
}
export interface FinancialStatements {
  income_expenditure: {
    title: string;
    start_date: string | null;
    end_date: string;
    sections: StatementSectionLines[];
    income: string;
    expenditure: string;
    surplus: string;
  };
  financial_position: {
    title: string;
    as_at: string;
    sections: StatementSectionLines[];
    assets: string;
    liabilities: string;
    equity: string;
    accumulated_surplus: string;
    funded_by: string;
    is_balanced: boolean;
  };
  changes_in_equity: {
    title: string;
    start_date: string | null;
    end_date: string;
    opening_date: string | null;
    accounts: Array<{
      id: number;
      code: string;
      name: string;
      opening: string;
      movement: string;
      closing: string;
    }>;
    opening_surplus: string;
    surplus: string;
    closing_surplus: string;
    opening_funds: string;
    closing_funds: string;
    agrees_with_balance_sheet: boolean;
  };
  /** Main accounts nobody has placed yet - listed rather than silently dropped. */
  unclassified: { accounts: StatementLine[]; total: string; count: number };
  comparative: { start_date: string | null; end_date: string } | null;
}
export interface StatementCategory {
  id: number;
  section: number;
  section_name: string;
  statement: "financial_position" | "income_expenditure";
  name: string;
  display_order: number;
  account_count: number;
}
export interface StatementSection {
  id: number;
  name: string;
  statement: "financial_position" | "income_expenditure";
  account_types: string[];
  permitted_account_types: string[];
  display_order: number;
  categories: StatementCategory[];
}
export interface AccountingReport {
  trial_balance: {
    as_at?: string | null;
    accounts: Array<{
      id: number;
      code: string;
      name: string;
      account_type: string;
      debit: string;
      credit: string;
      trial_debit: string;
      trial_credit: string;
      balance: string;
      rollup_debit: string;
      rollup_credit: string;
      rollup_balance: string;
      direct_balance: string;
      has_children: boolean;
      parent?: number | null;
      depth: number;
      is_subaccount: boolean;
      is_control_account: boolean;
      is_reporting_account: boolean;
      holds_a_balance_of_its_own: boolean;
      status: "active" | "inactive" | "closed" | "archived";
      is_active: boolean;
    }>;
    total_debit: string;
    total_credit: string;
    is_balanced: boolean;
  };
  income_statement: { income: string; expenses: string; surplus: string };
  balance_sheet: {
    assets: string;
    liabilities: string;
    equity: string;
    current_surplus: string;
    liabilities_and_equity: string;
  };
}
export interface BudgetVarianceReport {
  id: number;
  name: string;
  status: string;
  workflow_status?: string;
  start_date: string;
  end_date: string;
  prepared_by_name?: string;
  reviewed_by_name?: string;
  reviewed_at?: string | null;
  approved_by_name?: string;
  approved_at?: string | null;
  total_budget: string;
  total_actual: string;
  total_variance: string;
  utilization_percent: string;
  lines: Array<{ account: number; code: string; name: string; budget: string; actual: string; variance: string }>;
}

export interface AgeingDetailRow {
  invoice_id: number;
  invoice_number: string;
  /** The student's id, or the supplier's on the payables report. */
  student_id: number;
  /** The student's name, or the supplier's on the payables report. */
  student_name: string;
  /** Receivables only: the learner's number and current class. */
  student_code?: string;
  class_name?: string;
  due_date: string;
  balance: string;
  days_overdue: number;
  bucket: string;
}
export interface AgeingReport {
  as_of: string;
  buckets: { current: string; "1_30": string; "31_60": string; "61_90": string; over_90: string };
  total: string;
  detail: AgeingDetailRow[];
}
export interface LedgerTransaction {
  posted_to_id?: number;
  posted_to_code?: string;
  posted_to_name?: string;
  date: string;
  entry_id: number;
  entry_number: string;
  description: string;
  reference: string;
  debit: string;
  credit: string;
  balance: string;
  source_type: string;
  source_id: string;
  corresponding_accounts: Array<{
    account_id: number;
    account_code: string;
    account_name: string;
    debit: string;
    credit: string;
  }>;
}
export interface AccountLedgerReport {
  account: {
    id: number;
    code: string;
    name: string;
    account_type: string;
    is_reporting_account?: boolean;
    is_subaccount?: boolean;
    parent?: number | null;
  };
  includes_subaccounts?: boolean;
  /** Dates the opening and closing balance rows belong to. */
  opening_date?: string;
  closing_date?: string;
  subaccounts?: Array<{ id: number; code: string; name: string; is_reporting_account: boolean }>;
  opening_balance: string;
  transactions: LedgerTransaction[];
  closing_balance: string;
}
export interface StatementEvent {
  date: string;
  type: string;
  reference: string | number;
  description: string;
  debit: string;
  credit: string;
  balance: string;
}
/** One receipt in a student's payment history, and where its money went. */
/** One of the signed-in staff member's own payslips, from an approved run. */
export interface MyPayslip {
  id: number;
  period_name: string;
  period_start: string;
  period_end: string;
  gross_salary: string;
  paye_tax: string;
  nssf_employee: string;
  local_service_tax: string;
  other_deductions: string;
  net_pay: string;
}
export interface StatementReceipt {
  date: string;
  receipt_number: string;
  method: string;
  reference: string;
  amount: string;
  applied: string;
  held_as_credit: string;
  paid_directly: string;
  refunded: string;
}
export interface StudentStatementReport {
  student: { id: number; name: string; student_id: string };
  as_of: string;
  events: StatementEvent[];
  outstanding_balance: string;
  receipts?: StatementReceipt[];
  credit_balance?: string;
  balance_after_credit?: string;
}
export interface SupplierStatementReport {
  supplier: { id: number; name: string };
  as_of: string;
  events: StatementEvent[];
  outstanding_balance: string;
}
export interface CashFlowReport {
  start_date: string;
  end_date: string;
  receipts: Array<{ source: string; amount: string }>;
  payments: Array<{ source: string; amount: string }>;
  total_receipts: string;
  total_payments: string;
  net_cash_movement: string;
}
export interface ReconciliationBookItem {
  journal_entry: number;
  entry_number: string;
  date: string;
  description: string;
  reference: string;
  amount: string;
  days_waiting: number;
}
export interface ReconciliationStatementItem {
  id: number;
  date: string;
  description: string;
  reference: string;
  amount: string;
  /** Typed in by hand rather than imported from the bank's file. */
  manual: boolean;
  days_waiting: number;
}
export interface BankDeposit {
  id: number;
  bank_account: number;
  bank_account_name: string;
  source_account: number;
  source_account_code: string;
  source_account_name: string;
  amount: string;
  deposit_date: string;
  reference: string;
  notes: string;
  recorded_by_name: string;
  entry_number: string | null;
  /** The bank statement shows it, so it can no longer be cancelled. */
  on_statement: boolean;
  cancelled_by_name: string | null;
  cancelled_at: string | null;
  cancellation_reason: string;
}
export interface BankSignOff {
  id: number;
  bank_account: number;
  bank_account_name: string;
  as_at: string;
  statement_balance: string;
  book_balance: string;
  adjusted_balance: string;
  items_waiting: number;
  oldest_item_days: number | null;
  notes: string;
  workflow_status: string;
  prepared_by_name: string;
  reviewed_by_name: string | null;
  signed_by_name: string | null;
  approved_at: string | null;
  return_reason: string;
  rejection_reason: string;
  withdrawn_by_name: string | null;
  withdrawn_at: string | null;
  withdrawal_reason: string;
}
export interface ReconciliationSignOffState {
  signed: {
    id: number; as_at: string; prepared_by: string | null; signed_by: string | null; signed_at: string | null;
    statement_balance: string;
    /** False when something dated inside the signed period has changed since. */
    still_holds: boolean;
  } | null;
  pending: { id: number; as_at: string; workflow_status: string } | null;
}
/** The bank's balance against the books', with every item between them. */
export interface BankReconciliationStatement {
  bank_account: { id: number; name: string; bank_name: string };
  start_date: string | null;
  end_date: string;
  sign_off: ReconciliationSignOffState | null;
  statement_balance: string;
  /** "statement": the bank's own balance; "lines": the opening balance plus the lines imported. */
  statement_balance_source: "statement" | "lines";
  statement_balance_date: string | null;
  opening_balance: string;
  lines_balance: string;
  lines_differ_by: string | null;
  deposits_not_on_statement: ReconciliationBookItem[];
  payments_not_on_statement: ReconciliationBookItem[];
  adjusted_statement_balance: string;
  book_balance: string;
  money_in_not_in_books: ReconciliationStatementItem[];
  money_out_not_in_books: ReconciliationStatementItem[];
  adjusted_book_balance: string;
  difference: string;
  is_reconciled: boolean;
  matched: Array<{ id: number; date: string; description: string; amount: string; entry_number: string; reconciled_by: string | null; reconciled_at: string | null; manual: boolean }>;
}
export interface FeeCollectionReport {
  start_date: string;
  end_date: string;
  by_fee_type: Array<{ fee_type: string; amount: string }>;
  total_collected: string;
  payment_count: number;
}
export interface OutstandingFeesReport {
  as_of: string;
  by_class: Array<{ class_name: string; amount: string }>;
  by_student: Array<{ student_pk: number; student_code: string; student_name: string; class_name: string; balance: string; invoice_count: number }>;
  total: string;
  detail: Array<{ student_id: number; student_name: string; class_name: string; invoice_number: string; due_date: string; balance: string }>;
}
export interface TotalReceivablesReport {
  as_of: string;
  total_receivables: string;
  outstanding_invoice_count: number;
}
export interface ReceivablesControlAccountReconciliation {
  control_account: { id: number | null; code: string; name: string };
  subsidiary_ledger_total: string;
  control_account_balance: string;
  difference: string;
  is_reconciled: boolean;
  student_count: number;
  students: Array<{
    student_id: number;
    student_name: string;
    charges: string;
    cancellations: string;
    payments: string;
    refunds: string;
    credits: string;
    balance: string;
  }>;
}
export interface ReceivablesControlReconciliationReport {
  as_of: string;
  control_accounts: ReceivablesControlAccountReconciliation[];
}
export interface PayablesControlAccountReconciliation {
  control_account: { id: number | null; code: string; name: string };
  subsidiary_ledger_total: string;
  control_account_balance: string;
  difference: string;
  is_reconciled: boolean;
  supplier_count: number;
  suppliers: Array<{
    supplier_id: number;
    supplier_name: string;
    bills: string;
    payments: string;
    balance: string;
    bill_count: number;
    payment_count: number;
  }>;
}
export interface PayablesControlReconciliationReport {
  as_of: string;
  control_accounts: PayablesControlAccountReconciliation[];
}
export interface AuditApprovalEvent {
  id: number;
  actor: number;
  actor_name: string;
  action: string;
  action_display: string;
  from_status: string;
  to_status: string;
  comment: string;
  created_at: string;
  document_type: string;
  object_id: number;
}
export interface AuditApprovalReport {
  start_date: string | null;
  end_date: string | null;
  events: AuditApprovalEvent[];
}
export interface Grant extends WorkflowFields {
  id: number;
  name: string;
  donor: string;
  project?: string;
  restriction: "unrestricted" | "temporarily_restricted" | "restricted";
  funding_period_start: string;
  funding_period_end: string;
  total_amount: string;
  received_amount: string;
  spent_amount: string;
  balance: string;
  status: "active" | "closed";
  notes?: string;
  prepared_by?: number;
  prepared_by_name?: string;
  approved_by_name?: string;
  approved_at?: string | null;
}
export interface GrantTransaction {
  id: number;
  grant: number;
  transaction_type: "receipt" | "expense";
  amount: string;
  transaction_date: string;
  description?: string;
  account: number;
  account_name?: string;
  journal_entry: number;
  recorded_by?: number;
  recorded_by_name?: string;
  created_at: string;
}
export interface GrantSummaryReport {
  as_of: string;
  total_grants: number;
  total_committed: string;
  total_received: string;
  total_spent: string;
  detail: Array<{
    id: number;
    name: string;
    donor: string;
    project?: string;
    restriction: string;
    status: string;
    workflow_status: string;
    funding_period_start: string;
    funding_period_end: string;
    total_amount: string;
    received_amount: string;
    spent_amount: string;
    balance: string;
  }>;
}
export interface GrantStatementEvent {
  date: string;
  type: "receipt" | "expense";
  description: string;
  account: string;
  receipt: string;
  expense: string;
  balance: string;
}
export interface GrantStatementReport {
  grant: { id: number; name: string; donor: string; restriction: string };
  events: GrantStatementEvent[];
  closing_balance: string;
}
export type GovernanceScopeStatus = "active" | "not_started" | "expired";
export interface GovernanceScopeChange {
  id: number;
  action: "created" | "changed" | "ended";
  changes: string[];
  reason: string;
  changed_by_name: string;
  created_at: string;
}
export interface GovernanceAccessScope {
  id: number;
  user: number;
  user_name: string;
  username: string;
  user_type: string;
  user_is_active: boolean;
  can_view_financial_position: boolean;
  can_view_budgets: boolean;
  can_view_approvals: boolean;
  can_view_exceptions: boolean;
  can_drill_down: boolean;
  can_view_salary: boolean;
  grant_scope: "none" | "selected" | "all";
  grants: number[];
  grant_names: string[];
  valid_from?: string | null;
  valid_until?: string | null;
  status: GovernanceScopeStatus;
  configured_by_name?: string;
  updated_at: string;
  history: GovernanceScopeChange[];
}
/** A board or donor login, whether or not its access has been set up. */
export interface GovernanceAccount {
  id: number;
  name: string;
  username: string;
  user_type: "board_viewer" | "donor_viewer";
  is_active: boolean;
  scope: number | null;
}
export type GovernanceScopeChanges = Partial<Pick<GovernanceAccessScope,
  "can_view_financial_position" | "can_view_budgets" | "can_view_approvals" | "can_view_exceptions" |
  "can_drill_down" | "can_view_salary" | "grant_scope" | "grants" | "valid_from" | "valid_until">>;
export interface GovernanceBudget {
  id: number; name: string; start_date: string; end_date: string;
  budget_total: string; actual_total: string; variance: string;
  utilization_percent: string; approved_by: string; approved_at?: string; line_count: number;
  lines?: Array<{ account_code: string; account_name: string; account_type: string; budget: string; actual: string; variance: string; utilization_percent: string }>;
}
export interface GovernanceGrant {
  id: number; name: string; donor: string; project?: string; restriction: string; status: string;
  funding_period_start: string; funding_period_end: string; committed: string;
  received: string; spent: string; balance: string; utilization_percent: string;
  approved_by: string; approved_at?: string;
}
export interface GovernanceApproval { document_type: string; approved_by: string; approved_at: string; object_id?: number }
export interface GovernanceAccountBalance { id: number; code: string; name: string; account_type: string; depth: number; has_children: boolean; is_contra_account: boolean; balance: string }
export interface GovernanceStaffPay { staff: string; position: string; salary: string; allowances: string; gross: string; currency: string }
export interface GovernanceDashboard {
  period: { start: string; end: string };
  scope: Pick<GovernanceAccessScope, "can_view_financial_position" | "can_view_budgets" | "can_view_approvals" | "can_view_exceptions" | "can_drill_down" | "grant_scope" | "valid_from" | "valid_until">;
  financial_position: null | {
    as_of: string;
    period_start: string;
    assets: string;
    liabilities: string;
    net_assets: string;
    income: string;
    expenses: string;
    surplus_or_deficit: string;
    accounts?: GovernanceAccountBalance[];
  };
  budgets: null | GovernanceBudget[];
  approvals: null | {
    total: number;
    by_document_type: Array<{ content_type__model: string; count: number }>;
    recent: GovernanceApproval[];
  };
  exceptions: null | {
    overdue_invoices: number; overdue_amount: string; unreconciled_bank_lines: number;
    unresolved_provider_rows: number;
    /** null while the chart of accounts lacks the account the check needs; the note says which. */
    receivables_control_difference: string | null; receivables_control_note: string;
    payables_control_difference: string | null; payables_control_note: string;
  };
  grants: GovernanceGrant[];
  salary_summary?: {
    salaried_staff: number;
    monthly_salary_total: string;
    monthly_allowance_total: string;
    monthly_gross_total: string;
    staff?: GovernanceStaffPay[];
  };
}
export interface MyApprovalItem {
  document_type: string;
  id: number;
  title: string;
  amount: string | null;
  workflow_status: string;
  initiated_by: string;
  tab: string;
  actions: string[];
  created_at: string;
}
/** The document behind a queue entry, so whoever must sign it can read it. */
export interface ApprovalDetail {
  document_type: string;
  id: number;
  title: string;
  amount: string | null;
  workflow_status: string;
  tab: string;
  initiated_by: string;
  summary: { label: string; value: string }[];
  lines: { columns: string[]; rows: string[][]; total: string | null } | null;
  history: { action: string; actor: string; at: string; comment: string }[];
}
export interface FixedAssetRegisterReport {
  as_of: string;
  total_assets: number;
  total_cost: string;
  total_accumulated_depreciation: string;
  total_net_book_value: string;
  detail: Array<{
    id: number;
    asset_tag: string;
    name: string;
    category: string;
    supplier: string;
    purchase_date: string;
    cost: string;
    accumulated_depreciation: string;
    net_book_value: string;
    status: string;
    disposal_date: string | null;
  }>;
}

export interface AccountingPeriodReopenRequest extends WorkflowFields {
  id: number;
  period: number;
  period_name: string;
  reason: string;
  initiated_by: number;
  initiated_by_name: string;
  initiated_at: string;
  reviewed_by?: number;
  reviewed_by_name?: string;
  approved_by?: number;
  approved_by_name?: string;
}
export interface AccountingPeriod {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  is_closed: boolean;
  closed_by?: number;
  closed_by_name?: string | null;
  closed_at?: string;
  reopened_by?: number;
  reopened_by_name?: string | null;
  reopened_at?: string;
  latest_reopen_request?: AccountingPeriodReopenRequest | null;
}
export interface SupplierOption {
  id: number;
  name: string;
}
export interface BillablePurchaseOrderLine {
  id: number;
  item: number;
  item_name: string;
  quantity_ordered: number;
  quantity_received: number;
  unit_cost: string;
  total_cost: string;
}
/** A purchase order as the payables screen needs it: enough to attach a bill
 *  to it and itemise what is being charged for. The full order, and every way
 *  of changing one, stays in the inventory module. */
export interface BillablePurchaseOrder {
  id: number;
  order_number: string;
  supplier: number;
  supplier_name: string;
  status: string;
  order_date: string;
  total_amount: string;
  items: BillablePurchaseOrderLine[];
}
export interface VendorBillLine {
  id?: number;
  item?: number | null;
  item_name?: string | null;
  description: string;
  quantity: string;
  unit_amount: string;
  line_total?: string;
}
export interface BillMatch {
  matched: boolean;
  reason: string;
  purchase_order?: { id: number; order_number: string; status: string };
  lines: Array<{
    item: number | null;
    name: string;
    quantity_ordered: string;
    quantity_received: string;
    quantity_billed: string;
    amount_ordered: string;
    amount_billed: string;
  }>;
  blocking: string[];
  warnings: string[];
  ordered_total: string;
  billed_total: string;
}
export interface VendorBill extends WorkflowFields {
  id: number;
  supplier: number;
  supplier_name: string;
  supplier_payable_account_name?: string | null;
  purchase_order?: number | null;
  purchase_order_number?: string | null;
  lines?: VendorBillLine[];
  /** The school's own number for the bill (BILL-000001). */
  internal_number?: string;
  /** The number the supplier put on their invoice. */
  bill_number: string;
  bill_date: string;
  due_date: string;
  description: string;
  total_amount: string;
  paid_amount: string;
  balance_due: string;
  status: string;
  expense_account: number;
  payable_account?: number | null;
  prepared_by?: number;
  prepared_by_name?: string;
  approved_by_name?: string;
  approved_at?: string | null;
}
export interface BankAccount {
  id: number;
  name: string;
  bank_name: string;
  account_number: string;
  ledger_account: number;
  ledger_account_name: string;
  opening_balance: string;
  opening_balance_date?: string | null;
  is_active: boolean;
}
export interface BankLine {
  id: number;
  bank_account: number;
  transaction_date: string;
  description: string;
  reference: string;
  amount: string;
  /** The bank's own balance after this line, when its statement shows one. */
  balance?: string | null;
  import_batch?: number;
  import_filename?: string;
  matched_entry?: number | null;
  matched_entry_number?: string | null;
  matched_entry_description?: string | null;
  reconciled_by_name?: string | null;
  reconciled_at?: string | null;
}
export interface BankStatementImport {
  id: number;
  bank_account: number;
  bank_account_name: string;
  filename: string;
  status: "completed" | "partial" | "failed";
  total_rows: number;
  imported_rows: number;
  duplicate_rows: number;
  error_rows: number;
  errors: Array<{ row: number; message: string }>;
  /** Read, but worth a look: balance rows skipped, a running balance that does not follow. */
  warnings: Array<{ row: number | null; message: string }>;
  period_start: string | null;
  period_end: string | null;
  uploaded_by_name: string;
  created_at: string;
}
export interface BankMatchSuggestion {
  journal_entry: number;
  entry_number: string;
  entry_date: string;
  description: string;
  reference: string;
  amount: string;
  score: number;
  reasons: string[];
}
export interface BankLineSuggestions {
  bank_line: number;
  suggestions: BankMatchSuggestion[];
}
export interface ProviderPaymentImport {
  id: number;
  provider: string;
  filename: string;
  receipt_account: number;
  receipt_account_name: string;
  uploaded_by_name: string;
  status: "preview" | "partial" | "posted";
  detected_columns: Record<string, string>;
  total_rows: number;
  matched_rows: number;
  unmatched_rows: number;
  invalid_rows: number;
  duplicate_rows: number;
  posted_rows: number;
  provider_total: string;
  posted_total: string;
  settlement_total?: string | null;
  reconciliation: {
    provider_total: string;
    posted_total: string;
    settlement_total?: string | null;
    provider_to_system_difference: string;
    provider_to_settlement_difference?: string | null;
  };
  created_at: string;
}
export interface ProviderPaymentRow {
  id: number;
  import_batch: number;
  row_number: number;
  provider_transaction_id: string;
  transaction_date?: string | null;
  student_identifier: string;
  invoice_reference: string;
  payer_name: string;
  amount?: string | null;
  match_status: "matched" | "unmatched" | "invalid" | "duplicate";
  match_message: string;
  posting_status: "pending" | "posted" | "rejected";
  student?: number | null;
  student_name?: string;
  invoice?: number | null;
  invoice_number?: string;
  payment?: number | null;
}
export interface Budget extends WorkflowFields {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
  prepared_by?: number;
  prepared_by_name?: string;
  approved_by_name?: string;
  approved_at?: string | null;
  lines: Array<{
    id?: number;
    account: number;
    account_code?: string;
    account_name?: string;
    amount: string;
    notes: string;
  }>;
}

export interface Payslip {
  id: number;
  staff: number;
  staff_name: string;
  employee_id: string;
  position_title: string;
  basic_pay: string;
  allowances: string;
  gross_salary: string;
  paye_tax: string;
  nssf_employee: string;
  nssf_employer: string;
  local_service_tax: string;
  other_deductions: string;
  other_deductions_notes: string;
  net_pay: string;
  /** The residency PAYE was worked out on for this payslip. */
  tax_residency?: "resident" | "non_resident";
}
export interface ExcludedStaff {
  staff_id: number;
  employee_id: string;
  name: string;
}
export interface PayrollPeriod extends WorkflowFields {
  id: number;
  name: string;
  period_start: string;
  period_end: string;
  status: "draft" | "approved" | "cancelled";
  notes: string;
  prepared_by?: number;
  prepared_by_name?: string;
  approved_by?: number;
  approved_by_name?: string;
  approved_at?: string;
  /** Where the net pay was paid from. Only set once a payment is recorded --
   * approving a run no longer chooses an account. */
  payment_account?: number;
  payment_account_name?: string;
  journal_entry?: number;
  staff_count: number;
  total_net_pay: string;
  paid_amount: string;
  balance_due: string;
  /** "not_applicable" until the run is approved and actually owes anything. */
  payment_status: "not_applicable" | "unpaid" | "part_paid" | "paid";
  /** Active staff left out of the run because no salary was on file at generation. */
  excluded_staff: ExcludedStaff[];
  payslips: Payslip[];
  created_at: string;
}

/** What the school withholds from pay and hands on to someone else. */
export type StatutoryRole = "paye_payable" | "nssf_payable" | "lst_payable";

/**
 * A proposed movement of money against an approved run. Paying a payroll is a
 * transaction like any other, so it is raised, reviewed and approved before it
 * reaches the ledger -- `journal_entry` stays null until it is approved.
 */
export interface PayrollSettlement extends WorkflowFields {
  id: number;
  payroll_period: number | null;
  payroll_period_name?: string;
  components: { kind: string; role: string; label: string; amount: string }[];
  amount: string;
  payment_account: number;
  payment_account_name?: string;
  payment_date: string;
  reference: string;
  notes: string;
  journal_entry: number | null;
  initiated_by?: number;
  initiated_by_name?: string;
  created_at: string;
}

/** A payout of an approved advance. Money leaves the bank only once it is approved. */
export interface SalaryAdvancePayout extends WorkflowFields {
  id: number;
  advance: number;
  voucher_number?: string;
  payment_account: number;
  payment_account_name?: string;
  payment_date: string;
  reference: string;
  notes: string;
  journal_entry: number | null;
  initiated_by?: number;
  initiated_by_name?: string;
  approved_by_name?: string;
  created_at: string;
}

/**
 * Part of next month's pay, handed over early. Capped at 40% of take-home pay,
 * and taken back in full by the next payroll run.
 */
export interface SalaryAdvance extends WorkflowFields {
  id: number;
  staff: number;
  staff_name: string;
  employee_id: string;
  amount: string;
  reason: string;
  net_pay_basis: string;
  stage:
    | "awaiting_approval"
    | "approved"
    | "payout_pending"
    | "paid_out"
    | "recovered"
    | "rejected"
    | "cancelled";
  initiated_by?: number;
  initiated_by_name?: string;
  approved_by_name?: string;
  rejection_reason?: string;
  disbursements: SalaryAdvancePayout[];
  created_at: string;
}

export interface SalaryAdvancePosition {
  staff: number;
  staff_name: string;
  net_pay: string;
  limit: string;
  share: string;
  already_owed_or_requested: string;
  available: string;
  recoverable_now: string;
}

export interface PayrollPayment {
  id: number;
  payroll_period: number;
  payroll_period_name?: string;
  voucher_number?: string;
  payment_date: string;
  amount: string;
  reference: string;
  payment_account: number;
  payment_account_name?: string;
  journal_entry?: number;
  notes: string;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
}

export interface StatutoryBalance {
  role: StatutoryRole;
  label: string;
  account: string | null;
  account_name: string | null;
  /** null when no account is assigned to the role yet. */
  outstanding: string | null;
  configured: boolean;
  /** Approved runs that still owe this deduction. A month already paid is not
   * listed, because choosing it is how it would be paid twice. */
  runs: { id: number; name: string; outstanding: string }[];
}

export interface StatutoryRemittance {
  id: number;
  liability_role: StatutoryRole;
  liability_label?: string;
  voucher_number?: string;
  payment_date: string;
  amount: string;
  reference: string;
  payment_account: number;
  payment_account_name?: string;
  payroll_period?: number;
  payroll_period_name?: string;
  journal_entry?: number;
  notes: string;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
}

export const financialService = {
  getFees: async () => allRows<Fee>("/financial/fees/"),
  createFee: async (data: Partial<Fee>) =>
    (await api.post("/financial/fees/", data)).data,
  updateFee: async ({ id, ...data }: Partial<Fee> & { id: number }) =>
    (await api.patch(`/financial/fees/${id}/`, data)).data as Fee,
  deleteFee: async (id: number) => api.delete(`/financial/fees/${id}/`),
  getPayments: async (params?: ListParams) => allRows<LedgerPayment>(withParams("/financial/payments/", params)),
  getPaymentPage: async (params: ListParams) => getPage<LedgerPayment>("/financial/payments/", listParams(params)),
  getUnidentifiedReceipts: async () => allRows<UnidentifiedReceipt>("/financial/unidentified-receipts/"),
  createUnidentifiedReceipt: async (data: Partial<UnidentifiedReceipt>) =>
    (await api.post("/financial/unidentified-receipts/", data)).data,
  resolveUnidentifiedReceipt: async ({ id, ...data }: { id: number; student: number; invoice?: number; fee?: number; notes?: string }) =>
    (await api.post(`/financial/unidentified-receipts/${id}/resolve/`, data)).data,
  refundUnidentifiedReceipt: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/unidentified-receipts/${id}/refund/`, { reason })).data,
  /** `received_on`: the day the money reached the account, for anything but cash. */
  recordPayment: async (data: Partial<LedgerPayment> & { received_on?: string | null }) =>
    (await api.post("/financial/payments/", data)).data,
  requestRefund: async ({ id, amount, reason }: { id: number; amount: string; reason: string }) =>
    (await api.post(`/financial/payments/${id}/refund/`, { amount, reason })).data,
  getRefundRequests: async () => allRows<RefundRequest>("/financial/refund-requests/"),
  approveRefundRequest: async ({ id, notes = "" }: { id: number; notes?: string }) =>
    (await api.post(`/financial/refund-requests/${id}/approve/`, { notes })).data,
  rejectRefundRequest: async ({ id, notes }: { id: number; notes: string }) =>
    (await api.post(`/financial/refund-requests/${id}/reject/`, { notes })).data,
  cancelRefundRequest: async (id: number) =>
    (await api.post(`/financial/refund-requests/${id}/cancel/`)).data,
  getInvoices: async (params?: ListParams) => allRows<Invoice>(withParams("/financial/invoices/", params)),
  getInvoicePage: async (params: ListParams) => getPage<Invoice>("/financial/invoices/", listParams(params)),
  createInvoice: async (data: Partial<Invoice>) =>
    (await api.post("/financial/invoices/", data)).data,
  giveInvoiceDiscount: async ({ id, ...discount }: { id: number; reason: string; amount?: string; percent?: string }) =>
    (await api.post(`/financial/invoices/${id}/discount/`, discount)).data,
  applyInvoicePayment: async ({ id, payment }: { id: number; payment: number }) =>
    (await api.post(`/financial/invoices/${id}/apply-payment/`, { payment }))
      .data,
  reallocatePaymentAllocation: async ({ id, invoice, reason }: { id: number; invoice: number; reason: string }) =>
    (await api.post(`/financial/payment-allocations/${id}/reallocate/`, { invoice, reason })).data,
  cancelInvoice: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/invoices/${id}/cancel/`, { reason })).data,
  getClassLevels: async () => (await api.get("/financial/class-levels/")).data as string[],
  getFeeStructures: async () => allRows<FeeStructure>("/financial/fee-structures/"),
  createFeeStructure: async (data: {
    name: string;
    /** The AcademicTerm id. Its academic year comes with it, so that is not sent. */
    term: number;
    level: string;
    notes?: string;
    receivable_account?: number | null;
    items: Array<{ fee: number; amount: string }>;
  }) => (await api.post("/financial/fee-structures/", data)).data,
  deleteFeeStructure: async (id: number) =>
    api.delete(`/financial/fee-structures/${id}/`),
  generateInvoicesFromFeeStructure: async ({
    id,
    due_date,
    discounts,
  }: {
    id: number;
    due_date: string;
    discounts?: GenerationDiscount[];
  }) =>
    (await api.post(`/financial/fee-structures/${id}/generate-invoices/`, { due_date, discounts }))
      .data as GenerateInvoicesResult,
  getFeeStructureStudents: async (id: number) =>
    (await api.get(`/financial/fee-structures/${id}/students/`)).data as FeeStructureStudent[],
  getOnlinePaymentAvailability: async () =>
    (await api.get("/financial/online-payments/availability/")).data as OnlinePaymentAvailability,
  payInvoiceOnline: async (id: number, amount?: string) =>
    (await api.post(`/financial/invoices/${id}/pay-online/`, amount ? { amount } : {}))
      .data as OnlinePaymentInit,
  getOnlinePaymentStatus: async (orderTrackingId: string) =>
    (await api.get(`/financial/online-payments/${orderTrackingId}/status/`))
      .data as OnlinePaymentStatus,
  getCreditNotes: async () => allRows<CreditNote>("/financial/credit-notes/"),
  createCreditNote: async (data: { invoice: number; amount: string; reason: string }) =>
    (await api.post("/financial/credit-notes/", data)).data,
  updateCreditNote: async ({ id, ...data }: { id: number; amount?: string; reason?: string }) =>
    (await api.patch(`/financial/credit-notes/${id}/`, data)).data,
  submitCreditNote: async (id: number) =>
    (await api.post(`/financial/credit-notes/${id}/submit/`)).data,
  verifyCreditNote: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/credit-notes/${id}/verify/`, { comment })).data,
  returnCreditNote: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/credit-notes/${id}/return/`, { reason })).data,
  approveCreditNote: async (id: number) =>
    (await api.post(`/financial/credit-notes/${id}/approve/`)).data,
  rejectCreditNote: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/credit-notes/${id}/reject/`, { reason })).data,
  getStats: async () =>
    (await api.get("/financial/payments/statistics/")).data as FinancialStats,
  downloadPaymentReceipt: (paymentId: number) =>
    downloadReport(`/financial/payments/${paymentId}/`, "pdf"),
  /** Learners by name or number, including those who have left: a family can
   *  still owe, and pay, after their child is withdrawn or graduates. */
  searchStudents: async (query: string) => {
    if (query.trim().length < 2) return [] as StudentOption[];
    return (await api.get("/students/service-directory/", { params: { search: query, include_left: true } }))
      .data.results as StudentOption[];
  },
  getStudentsPage: async (params: ListParams & { page?: number; page_size?: number }) =>
    (await api.get("/students/service-directory/", { params: listParams(params) })).data as StudentDirectoryPage,
  getAllStudents: async (params?: ListParams) =>
    allRows<StudentOption>(withParams("/students/service-directory/", params)),
  getFinancialStatements: async (params?: {
    start_date?: string;
    end_date?: string;
    comparative_start_date?: string;
    comparative_end_date?: string;
  }) => (await api.get("/financial/financial-statements/", { params })).data as FinancialStatements,
  downloadFinancialStatement: (
    statement: "income_expenditure" | "financial_position" | "changes_in_equity",
    format: "csv" | "pdf",
    params?: Record<string, string | undefined>,
  ) => {
    const query = new URLSearchParams(
      Object.entries({ ...(params || {}), statement }).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value) acc[key] = String(value);
        return acc;
      }, {}),
    ).toString();
    return downloadReport(`/financial/financial-statements/?${query}`, format);
  },
  assignAccountStatementCategory: async (
    { id, statement_category }: { id: number; statement_category: number | "" },
  ) => (await api.post(`/financial/accounts/${id}/statement-category/`, { statement_category })).data as Account,
  getStatementSections: async () => allRows<StatementSection>("/financial/statement-sections/"),
  createStatementSection: async (data: Partial<StatementSection>) =>
    (await api.post("/financial/statement-sections/", data)).data as StatementSection,
  updateStatementSection: async ({ id, ...data }: { id: number } & Partial<StatementSection>) =>
    (await api.patch(`/financial/statement-sections/${id}/`, data)).data as StatementSection,
  deleteStatementSection: async (id: number) =>
    (await api.delete(`/financial/statement-sections/${id}/`)).data,
  createStatementCategory: async (data: { section: number; name: string }) =>
    (await api.post("/financial/statement-categories/", data)).data as StatementCategory,
  updateStatementCategory: async ({ id, ...data }: { id: number } & Partial<StatementCategory>) =>
    (await api.patch(`/financial/statement-categories/${id}/`, data)).data as StatementCategory,
  deleteStatementCategory: async (id: number) =>
    (await api.delete(`/financial/statement-categories/${id}/`)).data,
  reorderStatementTemplate: async (data: { sections?: number[]; categories?: Record<number, number[]> }) =>
    (await api.post("/financial/statement-template/reorder/", data)).data as StatementSection[],
  getAccounts: async () => allRows<Account>("/financial/accounts/"),
  getAccountRoles: async () =>
    (await api.get("/financial/account-roles/")).data as AccountRoleListing,
  proposeAccountRole: async ({ role, account }: { role: string; account: number }) =>
    (await api.put(`/financial/account-roles/${role}/`, { account })).data as AccountRoleAssignment,
  submitAccountRole: async (role: string) =>
    (await api.post(`/financial/account-roles/${role}/submit/`)).data as AccountRoleAssignment,
  submitAllAccountRoles: async () =>
    (await api.post("/financial/account-roles/submit-all/")).data as BulkRoleSubmission,
  // My Approvals acts on a role change by its id.
  verifyAccountRoleChange: async ({ id, comment }: { id: number; comment: string }) =>
    (await api.post(`/financial/account-role-changes/${id}/verify/`, { comment })).data as AccountRoleAssignment,
  returnAccountRoleChange: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/account-role-changes/${id}/return/`, { reason })).data as AccountRoleAssignment,
  rejectAccountRoleChange: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/account-role-changes/${id}/reject/`, { reason })).data as AccountRoleAssignment,
  approveAccountRoleChange: async (id: number) =>
    (await api.post(`/financial/account-role-changes/${id}/approve/`)).data as AccountRoleAssignment,
  verifyAllAccountRoles: async () =>
    (await api.post("/financial/account-roles/verify-all/")).data as BulkRoleSubmission,
  approveAllAccountRoles: async () =>
    (await api.post("/financial/account-roles/approve-all/")).data as BulkRoleSubmission,
  verifyAccountRole: async ({ role, comment }: { role: string; comment?: string }) =>
    (await api.post(`/financial/account-roles/${role}/verify/`, { comment })).data as AccountRoleAssignment,
  returnAccountRole: async ({ role, reason }: { role: string; reason: string }) =>
    (await api.post(`/financial/account-roles/${role}/return/`, { reason })).data as AccountRoleAssignment,
  rejectAccountRole: async ({ role, reason }: { role: string; reason: string }) =>
    (await api.post(`/financial/account-roles/${role}/reject/`, { reason })).data as AccountRoleAssignment,
  approveAccountRole: async (role: string) =>
    (await api.post(`/financial/account-roles/${role}/approve/`)).data as AccountRoleAssignment,
  createAccount: async (data: Partial<Account>) =>
    (await api.post("/financial/accounts/", data)).data,
  updateAccount: async ({ id, ...data }: { id: number } & Partial<Account>) =>
    (await api.patch(`/financial/accounts/${id}/`, data)).data as Account,
  deleteAccount: async (id: number) =>
    (await api.delete(`/financial/accounts/${id}/`)).data,
  submitAccount: async (id: number) =>
    (await api.post(`/financial/accounts/${id}/submit/`)).data,
  verifyAccount: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/accounts/${id}/verify/`, { comment })).data,
  returnAccount: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/accounts/${id}/return/`, { reason })).data,
  approveAccount: async (id: number) =>
    (await api.post(`/financial/accounts/${id}/approve/`)).data,
  rejectAccount: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/accounts/${id}/reject/`, { reason })).data,
  requestAccountStatusChange: async ({
    id,
    status,
    reason,
  }: {
    id: number;
    status: "active" | "inactive" | "closed" | "archived";
    reason: string;
  }) => (await api.post(`/financial/accounts/${id}/request-status-change/`, { status, reason })).data,
  getJournalEntries: async (params?: ListParams) => allRows<JournalEntry>(withParams("/financial/journal-entries/", params)),
  getJournalPage: async (params: ListParams) => getPage<JournalEntry>("/financial/journal-entries/", listParams(params)),
  createJournalEntry: async (data: {
    entry_date: string;
    description: string;
    reference?: string;
    transaction_type?: "general" | "internal_transfer" | "immediate_expense" | "correction";
    post_now?: boolean;
    lines: JournalLineInput[];
  }) => (await api.post("/financial/journal-entries/", data)).data,
  updateJournalEntry: async ({ id, ...data }: {
    id: number;
    entry_date?: string;
    description?: string;
    reference?: string;
    transaction_type?: "general" | "internal_transfer" | "immediate_expense" | "correction";
    lines?: JournalLineInput[];
  }) => (await api.patch(`/financial/journal-entries/${id}/`, data)).data,
  submitJournalEntry: async (id: number) =>
    (await api.post(`/financial/journal-entries/${id}/submit/`)).data,
  verifyJournalEntry: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/journal-entries/${id}/verify/`, { comment })).data,
  returnJournalEntry: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/journal-entries/${id}/return/`, { reason })).data,
  rejectJournalEntry: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/journal-entries/${id}/reject/`, { reason })).data,
  postJournalEntry: async (id: number) =>
    (await api.post(`/financial/journal-entries/${id}/post/`)).data,
  deleteJournalEntry: async (id: number) =>
    api.delete(`/financial/journal-entries/${id}/`),
  reverseJournalEntry: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/journal-entries/${id}/reverse/`, { reason })).data,
  getAccountingPeriods: async () => allRows<AccountingPeriod>('/financial/accounting-periods/'),
  createAccountingPeriod: async (data: Partial<AccountingPeriod>) =>
    (await api.post('/financial/accounting-periods/', data)).data,
  closeAccountingPeriod: async (id: number) =>
    (await api.post(`/financial/accounting-periods/${id}/close/`)).data,
  requestAccountingPeriodReopen: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/accounting-periods/${id}/request-reopen/`, { reason })).data,
  verifyAccountingPeriodReopen: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/accounting-period-reopen-requests/${id}/verify/`, { comment })).data,
  returnAccountingPeriodReopen: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/accounting-period-reopen-requests/${id}/return/`, { reason })).data,
  rejectAccountingPeriodReopen: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/accounting-period-reopen-requests/${id}/reject/`, { reason })).data,
  approveAccountingPeriodReopen: async (id: number) =>
    (await api.post(`/financial/accounting-period-reopen-requests/${id}/approve/`)).data,
  getAccountingReport: async (params?: { start_date?: string; end_date?: string }) =>
    (await api.get("/financial/accounting-reports/", { params })).data as AccountingReport,
  downloadAccountingReport: (
    report: "trial_balance" | "income_statement" | "balance_sheet",
    format: "csv" | "pdf",
    params?: { start_date?: string; end_date?: string },
  ) => {
    const query = new URLSearchParams({ ...(params || {}), report }).toString();
    return downloadReport(`/financial/accounting-reports/?${query}`, format);
  },

  // Reports (Phase 2)
  getAccountLedger: async (
    accountId: number,
    params?: { start_date?: string; end_date?: string; include_subaccounts?: boolean },
  ) => (await api.get(`/financial/reports/accounts/${accountId}/ledger/`, { params })).data as AccountLedgerReport,
  downloadAccountLedger: (
    accountId: number,
    format: "csv" | "pdf",
    params?: { start_date?: string; end_date?: string; include_subaccounts?: boolean },
  ) => {
    const query = new URLSearchParams(
      Object.entries(params || {}).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value !== undefined) acc[key] = String(value);
        return acc;
      }, {}),
    ).toString();
    return downloadReport(`/financial/reports/accounts/${accountId}/ledger/${query ? `?${query}` : ""}`, format);
  },
  getStudentStatement: async (studentId: number, params?: { as_of?: string }) =>
    (await api.get(`/financial/reports/students/${studentId}/statement/`, { params })).data as StudentStatementReport,
  downloadStudentStatement: (studentId: number, format: "csv" | "pdf", params?: { as_of?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return downloadReport(`/financial/reports/students/${studentId}/statement/${query ? `?${query}` : ""}`, format);
  },
  getReceivablesAgeing: async (params?: { as_of?: string }) =>
    (await api.get("/financial/reports/receivables-ageing/", { params })).data as AgeingReport,
  downloadReceivablesAgeing: (format: "csv" | "pdf", params?: { as_of?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return downloadReport(`/financial/reports/receivables-ageing/${query ? `?${query}` : ""}`, format);
  },
  getTotalReceivables: async () =>
    (await api.get("/financial/reports/total-receivables/")).data as TotalReceivablesReport,
  getReceivablesControlReconciliation: async (params?: { as_of?: string }) =>
    (await api.get("/financial/reports/receivables-control-reconciliation/", { params })).data as ReceivablesControlReconciliationReport,
  downloadReceivablesControlReconciliation: (format: "csv" | "pdf", params?: { as_of?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return downloadReport(`/financial/reports/receivables-control-reconciliation/${query ? `?${query}` : ""}`, format);
  },
  getSupplierStatement: async (supplierId: number, params?: { as_of?: string }) =>
    (await api.get(`/financial/reports/suppliers/${supplierId}/statement/`, { params })).data as SupplierStatementReport,
  downloadSupplierStatement: (supplierId: number, format: "csv" | "pdf", params?: { as_of?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return downloadReport(`/financial/reports/suppliers/${supplierId}/statement/${query ? `?${query}` : ""}`, format);
  },
  getPayablesAgeing: async (params?: { as_of?: string }) =>
    (await api.get("/financial/reports/payables-ageing/", { params })).data as AgeingReport,
  downloadPayablesAgeing: (format: "csv" | "pdf", params?: { as_of?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return downloadReport(`/financial/reports/payables-ageing/${query ? `?${query}` : ""}`, format);
  },
  getPayablesControlReconciliation: async (params?: { as_of?: string }) =>
    (await api.get("/financial/reports/payables-control-reconciliation/", { params })).data as PayablesControlReconciliationReport,
  downloadPayablesControlReconciliation: (format: "csv" | "pdf", params?: { as_of?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return downloadReport(`/financial/reports/payables-control-reconciliation/${query ? `?${query}` : ""}`, format);
  },
  getCashFlowReport: async (params: { start_date: string; end_date: string }) =>
    (await api.get("/financial/reports/cash-flow/", { params })).data as CashFlowReport,
  downloadCashFlowReport: (format: "csv" | "pdf", params: { start_date: string; end_date: string }) => {
    const query = new URLSearchParams(params).toString();
    return downloadReport(`/financial/reports/cash-flow/?${query}`, format);
  },
  getBankReconciliationStatement: async (bankAccountId: number, params?: { start_date?: string; end_date?: string }) =>
    (await api.get(`/financial/reports/bank-accounts/${bankAccountId}/reconciliation/`, { params })).data as BankReconciliationStatement,
  downloadBankReconciliationStatement: (bankAccountId: number, format: "csv" | "pdf", params?: { start_date?: string; end_date?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return downloadReport(`/financial/reports/bank-accounts/${bankAccountId}/reconciliation/${query ? `?${query}` : ""}`, format);
  },
  getFeeCollectionReport: async (params: { start_date: string; end_date: string }) =>
    (await api.get("/financial/reports/fee-collection/", { params })).data as FeeCollectionReport,
  downloadFeeCollectionReport: (format: "csv" | "pdf", params: { start_date: string; end_date: string }) => {
    const query = new URLSearchParams(params).toString();
    return downloadReport(`/financial/reports/fee-collection/?${query}`, format);
  },
  getOutstandingFeesReport: async () =>
    (await api.get("/financial/reports/outstanding-fees/")).data as OutstandingFeesReport,
  downloadOutstandingFeesReport: (format: "csv" | "pdf") => downloadReport("/financial/reports/outstanding-fees/", format),
  getAuditApprovalReport: async (params?: { start_date?: string; end_date?: string }) =>
    (await api.get("/financial/reports/audit-approvals/", { params })).data as AuditApprovalReport,
  downloadAuditApprovalReport: (format: "csv" | "pdf", params?: { start_date?: string; end_date?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return downloadReport(`/financial/reports/audit-approvals/${query ? `?${query}` : ""}`, format);
  },
  getSuppliers: async () => allRows<SupplierOption>("/financial/vendors/"),
  getBillablePurchaseOrders: async () =>
    allRows<BillablePurchaseOrder>("/financial/purchase-orders/"),
  createSupplier: async (data: {
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
    address?: string;
    payment_terms?: string;
  }) => (await api.post("/financial/vendors/", data)).data as SupplierOption,
  getVendorBills: async () => allRows<VendorBill>("/financial/vendor-bills/"),
  createVendorBill: async (data: Partial<VendorBill>) =>
    (await api.post("/financial/vendor-bills/", data)).data,
  updateVendorBill: async ({ id, ...data }: { id: number } & Partial<VendorBill>) =>
    (await api.patch(`/financial/vendor-bills/${id}/`, data)).data,
  submitVendorBill: async (id: number) =>
    (await api.post(`/financial/vendor-bills/${id}/submit/`)).data,
  verifyVendorBill: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/vendor-bills/${id}/verify/`, { comment })).data,
  returnVendorBill: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/vendor-bills/${id}/return/`, { reason })).data,
  rejectVendorBill: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/vendor-bills/${id}/reject/`, { reason })).data,
  approveVendorBill: async (id: number) =>
    (await api.post(`/financial/vendor-bills/${id}/approve/`)).data,
  payVendorBill: async ({
    id,
    ...data
  }: {
    id: number;
    amount: string;
    payment_account: number;
    reference: string;
    payment_date?: string;
  }) => (await api.post(`/financial/vendor-bills/${id}/pay/`, data)).data,
  getVendorBillMatch: async (id: number) =>
    (await api.get(`/financial/vendor-bills/${id}/match/`)).data as BillMatch,
  getBankAccounts: async () => allRows<BankAccount>("/financial/bank-accounts/"),
  createBankAccount: async (data: Partial<BankAccount>) =>
    (await api.post("/financial/bank-accounts/", data)).data,
  getBankLines: async () => allRows<BankLine>("/financial/bank-statement-lines/"),
  createBankLine: async (data: Partial<BankLine>) =>
    (await api.post("/financial/bank-statement-lines/", data)).data,
  reconcileBankLine: async ({
    id,
    journal_entry,
  }: {
    id: number;
    journal_entry: number;
  }) =>
    (
      await api.post(`/financial/bank-statement-lines/${id}/reconcile/`, {
        journal_entry,
      })
    ).data,
  /** Undo a match made in error; the reason goes to the audit log. */
  unmatchBankLine: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/bank-statement-lines/${id}/unmatch/`, { reason })).data,
  getBankStatementImports: async () =>
    allRows<BankStatementImport>("/financial/bank-statement-imports/"),
  importBankStatement: async ({ bank_account, file }: { bank_account: number; file: File }) => {
    const form = new FormData();
    form.append("bank_account", String(bank_account));
    form.append("file", file);
    return (
      await api.post("/financial/bank-statement-imports/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  getProviderPaymentImports: async () =>
    allRows<ProviderPaymentImport>("/financial/provider-payment-imports/"),
  getProviderPaymentRows: async (importBatch: number) =>
    allRows<ProviderPaymentRow>(`/financial/provider-payment-rows/?import_batch=${importBatch}`),
  importProviderPayments: async ({
    provider,
    receipt_account,
    settlement_total,
    period_start,
    period_end,
    column_mapping,
    file,
  }: {
    provider: string;
    receipt_account: number;
    settlement_total?: string;
    period_start?: string;
    period_end?: string;
    column_mapping?: Record<string, string>;
    file: File;
  }) => {
    const form = new FormData();
    form.append("provider", provider);
    form.append("receipt_account", String(receipt_account));
    if (settlement_total) form.append("settlement_total", settlement_total);
    if (period_start) form.append("period_start", period_start);
    if (period_end) form.append("period_end", period_end);
    if (column_mapping && Object.keys(column_mapping).length) {
      form.append("column_mapping", JSON.stringify(column_mapping));
    }
    form.append("file", file);
    return (
      await api.post("/financial/provider-payment-imports/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data as ProviderPaymentImport & { period_warnings?: string[] };
  },
  postProviderPaymentImport: async (id: number) =>
    (await api.post(`/financial/provider-payment-imports/${id}/post/`, {})).data,
  resolveProviderPaymentRow: async ({
    id,
    student,
    invoice,
  }: {
    id: number;
    student: number;
    invoice: number;
  }) =>
    (await api.post(`/financial/provider-payment-rows/${id}/resolve/`, { student, invoice })).data,
  getBankDeposits: async () => allRows<BankDeposit>("/financial/bank-deposits/"),
  /** Cash, cheques or a SurePay payout taken into a bank: one entry for the one line the bank shows. */
  recordBankDeposit: async (data: {
    bank_account: number; source_account: number; amount: string; deposit_date: string; reference: string; notes?: string;
  }) => (await api.post("/financial/bank-deposits/", data)).data as BankDeposit,
  cancelBankDeposit: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/bank-deposits/${id}/cancel/`, { reason })).data as BankDeposit,
  /** Enter a charge the bank took, from its imported statement line, and match it. */
  recordBankCharge: async (id: number) =>
    (await api.post(`/financial/bank-statement-lines/${id}/record-charge/`)).data as BankLine,
  getBankSignOffs: async (bankAccountId: number) =>
    allRows<BankSignOff>(`/financial/bank-accounts/${bankAccountId}/sign-offs/`),
  prepareBankSignOff: async ({ bankAccountId, as_at, notes }: { bankAccountId: number; as_at: string; notes?: string }) =>
    (await api.post(`/financial/bank-accounts/${bankAccountId}/sign-offs/`, { as_at, notes })).data as BankSignOff,
  verifyBankSignOff: async ({ id, comment }: { id: number; comment: string }) =>
    (await api.post(`/financial/bank-sign-offs/${id}/verify/`, { comment })).data as BankSignOff,
  returnBankSignOff: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/bank-sign-offs/${id}/return/`, { reason })).data as BankSignOff,
  rejectBankSignOff: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/bank-sign-offs/${id}/reject/`, { reason })).data as BankSignOff,
  approveBankSignOff: async (id: number) =>
    (await api.post(`/financial/bank-sign-offs/${id}/approve/`)).data as BankSignOff,
  withdrawBankSignOff: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/bank-sign-offs/${id}/withdraw/`, { reason })).data as BankSignOff,
  getBankSuggestions: async () =>
    (await api.get("/financial/bank-reconciliation-suggestions/")).data as BankLineSuggestions[],
  autoMatchBankLines: async (bank_account: number) =>
    (await api.post("/financial/bank-reconciliation-auto-match/", { bank_account })).data,
  getBudgets: async () => allRows<Budget>("/financial/budgets/"),
  createBudget: async (data: Omit<Budget, "id" | "status">) =>
    (await api.post("/financial/budgets/", data)).data,
  updateBudget: async ({ id, ...data }: { id: number } & Partial<Omit<Budget, "id" | "status">>) =>
    (await api.patch(`/financial/budgets/${id}/`, data)).data,
  submitBudget: async (id: number) =>
    (await api.post(`/financial/budgets/${id}/submit/`)).data,
  verifyBudget: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/budgets/${id}/verify/`, { comment })).data,
  returnBudget: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/budgets/${id}/return/`, { reason })).data,
  rejectBudget: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/budgets/${id}/reject/`, { reason })).data,
  approveBudget: async (id: number) =>
    (await api.post(`/financial/budgets/${id}/approve/`)).data,
  getBudgetVariance: async (id: number) =>
    (await api.get(`/financial/budgets/${id}/variance/`)).data as BudgetVarianceReport,
  downloadBudgetVariance: (id: number, format: "csv" | "pdf") =>
    downloadReport(`/financial/budgets/${id}/variance/`, format),
  getGrants: async () => allRows<Grant>("/financial/grants/"),
  createGrant: async (
    data: Omit<Grant, "id" | "status" | "received_amount" | "spent_amount" | "balance">,
  ) => (await api.post("/financial/grants/", data)).data,
  updateGrant: async ({ id, ...data }: { id: number } & Partial<Grant>) =>
    (await api.patch(`/financial/grants/${id}/`, data)).data,
  submitGrant: async (id: number) => (await api.post(`/financial/grants/${id}/submit/`)).data,
  verifyGrant: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/grants/${id}/verify/`, { comment })).data,
  returnGrant: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/grants/${id}/return/`, { reason })).data,
  rejectGrant: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/grants/${id}/reject/`, { reason })).data,
  approveGrant: async (id: number) => (await api.post(`/financial/grants/${id}/approve/`)).data,
  getGrantTransactions: async (grantId: number) =>
    allRows<GrantTransaction>(`/financial/grants/${grantId}/transactions/`),
  recordGrantReceipt: async ({
    id,
    ...data
  }: {
    id: number;
    amount: string;
    receipt_account: number;
    transaction_date?: string;
    description?: string;
  }) => (await api.post(`/financial/grants/${id}/receipt/`, data)).data,
  recordGrantExpense: async ({
    id,
    ...data
  }: {
    id: number;
    amount: string;
    expense_account: number;
    payment_account: number;
    transaction_date?: string;
    description?: string;
  }) => (await api.post(`/financial/grants/${id}/expense/`, data)).data,
  getMyApprovals: async () =>
    (await api.get("/financial/my-approvals/")).data as { items: MyApprovalItem[] },
  getApprovalDetail: async ({ documentType, id }: { documentType: string; id: number }) =>
    (await api.get(`/financial/my-approvals/${documentType}/${id}/`)).data as ApprovalDetail,
  getGovernanceDashboard: async (params: { date_from?: string; date_to?: string } = {}) =>
    (await api.get("/financial/governance/dashboard/", { params })).data as GovernanceDashboard,
  getGovernanceScopes: async () =>
    allRows<GovernanceAccessScope>("/financial/governance/scopes/"),
  getGovernanceAccounts: async () =>
    (await api.get("/financial/governance/accounts/")).data as GovernanceAccount[],
  createGovernanceScope: async (data: { user: number; reason?: string }) =>
    (await api.post("/financial/governance/scopes/", data)).data as GovernanceAccessScope,
  updateGovernanceScope: async ({ id, ...data }: { id: number; reason: string } & GovernanceScopeChanges) =>
    (await api.patch(`/financial/governance/scopes/${id}/`, data)).data as GovernanceAccessScope,
  endGovernanceScope: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/governance/scopes/${id}/end/`, { reason })).data as GovernanceAccessScope,
  getFixedAssetRegisterReport: async () =>
    (await api.get("/financial/reports/fixed-assets/")).data as FixedAssetRegisterReport,
  downloadFixedAssetRegisterReport: (format: "csv" | "pdf") =>
    downloadReport("/financial/reports/fixed-assets/", format),
  getGrantSummaryReport: async () =>
    (await api.get("/financial/reports/grants/")).data as GrantSummaryReport,
  downloadGrantSummaryReport: (format: "csv" | "pdf") =>
    downloadReport("/financial/reports/grants/", format),
  getGrantStatementReport: async (grantId: number, params?: { start_date?: string; end_date?: string }) =>
    (await api.get(`/financial/reports/grants/${grantId}/statement/`, { params })).data as GrantStatementReport,
  downloadGrantStatementReport: (
    grantId: number,
    format: "csv" | "pdf",
    params?: { start_date?: string; end_date?: string },
  ) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return downloadReport(`/financial/reports/grants/${grantId}/statement/${query ? `?${query}` : ""}`, format);
  },
  getPayrollPeriods: async () => allRows<PayrollPeriod>("/financial/payroll-periods/"),
  generatePayrollPeriod: async (data: { name: string; period_start: string; period_end: string; notes?: string }) =>
    (await api.post("/financial/payroll-periods/", data)).data,
  submitPayrollPeriod: async (id: number) =>
    (await api.post(`/financial/payroll-periods/${id}/submit/`)).data,
  verifyPayrollPeriod: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/payroll-periods/${id}/verify/`, { comment })).data,
  returnPayrollPeriod: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/payroll-periods/${id}/return/`, { reason })).data,
  rejectPayrollPeriod: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/payroll-periods/${id}/reject/`, { reason })).data,
  /** Approving recognises what the run owes. It moves no cash, so it takes no
   * payment account -- see payPayrollPeriod. */
  approvePayrollPeriod: async ({ id }: { id: number }) =>
    (await api.post(`/financial/payroll-periods/${id}/approve/`)).data,
  payPayrollPeriod: async ({ id, ...data }: {
    id: number; amount: string; payment_account: number;
    reference: string; payment_date?: string; notes?: string;
  }) => (await api.post(`/financial/payroll-periods/${id}/pay/`, data)).data as PayrollSettlement,
  getSalaryAdvances: async () => allRows<SalaryAdvance>("/financial/salary-advances/"),
  /** How much someone may still borrow. Yours, unless finance names a staff member. */
  getSalaryAdvancePosition: async (staff?: number) =>
    (await api.get(`/financial/salary-advances/position/${staff ? `?staff=${staff}` : ""}`))
      .data as SalaryAdvancePosition,
  requestSalaryAdvance: async (data: { amount: string; reason: string; staff?: number }) =>
    (await api.post("/financial/salary-advances/", data)).data as SalaryAdvance,
  approveSalaryAdvance: async ({ id }: { id: number }) =>
    (await api.post(`/financial/salary-advances/${id}/approve/`)).data as SalaryAdvance,
  rejectSalaryAdvance: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/salary-advances/${id}/reject/`, { reason })).data as SalaryAdvance,
  cancelSalaryAdvance: async ({ id }: { id: number }) =>
    (await api.post(`/financial/salary-advances/${id}/cancel/`)).data as SalaryAdvance,
  /** Raise the payout of an approved advance. It waits for review and approval. */
  payOutSalaryAdvance: async ({ id, ...data }: {
    id: number; payment_account: number; reference: string; payment_date?: string; notes?: string;
  }) => (await api.post(`/financial/salary-advances/${id}/disburse/`, data)).data as SalaryAdvancePayout,
  verifyAdvancePayout: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/salary-advance-payouts/${id}/verify/`, { comment })).data,
  returnAdvancePayout: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/salary-advance-payouts/${id}/return/`, { reason })).data,
  rejectAdvancePayout: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/salary-advance-payouts/${id}/reject/`, { reason })).data,
  cancelAdvancePayout: async ({ id }: { id: number }) =>
    (await api.post(`/financial/salary-advance-payouts/${id}/cancel/`)).data,
  /** Signing this off is the point the advance actually leaves the bank. */
  approveAdvancePayout: async ({ id }: { id: number }) =>
    (await api.post(`/financial/salary-advance-payouts/${id}/approve/`)).data,
  /** Pay one part of a run -- its net pay, or one of its deductions. */
  payPayrollComponent: async ({ id, ...data }: {
    id: number; role: string; amount: string; payment_account: number;
    reference: string; payment_date?: string; notes?: string;
  }) => (await api.post(`/financial/payroll-periods/${id}/pay-component/`, data)).data as PayrollSettlement,
  getPayrollPayments: async (payrollPeriod?: number) =>
    allRows<PayrollPayment>(
      payrollPeriod
        ? `/financial/payroll-payments/?payroll_period=${payrollPeriod}`
        : "/financial/payroll-payments/",
    ),
  getStatutoryBalances: async () =>
    (await api.get("/financial/statutory-balances/")).data as StatutoryBalance[],
  getStatutoryRemittances: async () =>
    allRows<StatutoryRemittance>("/financial/statutory-remittances/"),
  /** What one bank transfer would cover for a run: net pay plus each deduction
   * still owed on it. */
  getPayrollSettlementPreview: async (id: number) =>
    (await api.get(`/financial/payroll-periods/${id}/settlement-preview/`)).data as {
      components: { kind: string; role: string; label: string; amount: string }[];
      total: string;
      settled: { role: string; label: string; amount: string; vouchers: string[] }[];
    },
  /** Settle net pay and every deduction for a run in a single transaction. */
  settlePayrollPeriod: async ({ id, ...data }: {
    id: number;
    payment_account: number;
    reference: string;
    payment_date?: string;
    notes?: string;
  }) => (await api.post(`/financial/payroll-periods/${id}/settle/`, data)).data as {
    settlement: PayrollSettlement;
    total: string;
    period: PayrollPeriod;
  },
  recordStatutoryRemittance: async (data: {
    liability_role: StatutoryRole; amount: string; payment_account: number;
    reference: string; payment_date?: string; payroll_period?: number; notes?: string;
  }) => (await api.post("/financial/statutory-remittances/record/", data)).data as PayrollSettlement,
  /** Payments raised against payroll, at whatever stage of approval they are at. */
  getPayrollSettlements: async (payrollPeriod?: number) =>
    allRows<PayrollSettlement>(
      payrollPeriod
        ? `/financial/payroll-settlements/?payroll_period=${payrollPeriod}`
        : "/financial/payroll-settlements/",
    ),
  verifyPayrollSettlement: async ({ id, comment }: { id: number; comment?: string }) =>
    (await api.post(`/financial/payroll-settlements/${id}/verify/`, { comment })).data,
  returnPayrollSettlement: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/payroll-settlements/${id}/return/`, { reason })).data,
  rejectPayrollSettlement: async ({ id, reason }: { id: number; reason: string }) =>
    (await api.post(`/financial/payroll-settlements/${id}/reject/`, { reason })).data,
  cancelPayrollSettlement: async ({ id, reason }: { id: number; reason?: string }) =>
    (await api.post(`/financial/payroll-settlements/${id}/cancel/`, { reason })).data,
  /** Signing this off is the point money actually leaves the bank. */
  approvePayrollSettlement: async ({ id }: { id: number }) =>
    (await api.post(`/financial/payroll-settlements/${id}/approve/`)).data,
  /** Put another deduction on one payslip while the run is still being prepared. */
  setPayslipDeductions: async ({ id, ...data }: {
    id: number; other_deductions: string; other_deductions_notes: string;
  }) => (await api.post(`/financial/payslips/${id}/deductions/`, data)).data as Payslip,
  /** One staff member's payslip as a PDF. */
  getMyPayslips: async () => (await api.get("/financial/payslips/mine/")).data as MyPayslip[],
  downloadPayslipPdf: (id: number) =>
    downloadFile(`/financial/payslips/${id}/pdf/`, "payslip.pdf"),
  /** Every payslip in a run, one per page, for a single print job. */
  downloadPayrollPayslipsPdf: (id: number) =>
    downloadFile(`/financial/payroll-periods/${id}/payslips/pdf/`, "payslips.pdf"),
  /** Bulk bank credit file for an approved run. Refuses if anyone lacks bank details. */
  downloadPayrollPaymentFile: (id: number) =>
    downloadFile(`/financial/payroll-periods/${id}/payment-file/`, "bank-payment.csv"),
  cancelPayrollPeriod: async (id: number) =>
    (await api.post(`/financial/payroll-periods/${id}/cancel/`)).data,
};
